import { Injectable } from '@angular/core';
import { environment } from '@reenstudio/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { ApiParserService } from '@reenstudio/app/core/services/api/parser/api-parser.service';

@Injectable({
  providedIn: 'root',
})
export class AbstractApiService {
  /**
   * @description Path di base
   * @private
   */
  private _host: string = environment.api;

  /**
   * @description Header di default
   * @private
   */
  private _defaultHeaders: any = {
    'Content-Type': 'application/json',
    'x-client': 'WEB',
  };

  /**
   * @description Formato data richiesto dalle api
   * @private
   */
  private _dateFormat: string = 'YYYY-MM-dd HH:mm:ss';

  constructor(protected http: HttpClient, protected logger: NGXLogger) {}

  /**
   * @description Fetch dati. Usare il Generic "T" per definire il tipo di dati che ritorna la chiamata
   * @param endpoint
   * @param params
   * @param includePagination
   * @param includeMeta
   * @param includeCount
   * @param onlyPagination
   * @protected
   */
  protected fetch<T>(
    endpoint: string,
    params: any = null,
    includePagination: boolean = false,
    includeMeta: boolean = false,
    includeCount: boolean = false,
    onlyPagination: boolean = false
  ) {
    return this.http
      .get(this.getUrl(endpoint), {
        headers: this.getHeaders(
          includePagination,
          includeMeta,
          includeCount,
          onlyPagination
        ),
        params: ApiParserService.parseParams(params),
      })
      .pipe(
        map((res) => {
          this.logger.debug(res);
          return <T>res;
        }),
        catchError((err) => {
          this.logger.error(err);
          throw err;
        })
      );
  }

  /**
   * Fetch dati. Usare il Generic "T" per definire il tipo di dati in ingresso ed il Generic "K" per il tipo di dati in uscita
   * @param endpoint
   * @param data
   * @protected
   */
  protected post<T, K>(
    endpoint: string,
    data: T,
    includePagination: boolean = false,
    includeMeta: boolean = false,
    includeCount: boolean = false,
    onlyPagination: boolean = false,
    setAction: string = ''
  ) {
    return this.http
      .post(this.getUrl(endpoint), data, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((res) => {
          this.logger.debug(res);
          return <K>res;
        }),
        catchError((err) => {
          this.logger.error(err);
          throw err;
        })
      );
  }

  /**
   * Patch dati. Usare il Generic "T" per definire il tipo di dati in ingresso ed il Generic "K" per il tipo di dati in uscita
   * @param endpoint
   * @param data
   * @protected
   */
  protected patch<T, K>(
    endpoint: string,
    data?: T,
    includePagination: boolean = false,
    includeMeta: boolean = false,
    includeCount: boolean = false,
    onlyPagination: boolean = false,
    setAction: string = ''
  ) {
    return this.http
      .patch(this.getUrl(endpoint), data, {
        headers: this.getHeaders(
          includePagination,
          includeMeta,
          includeCount,
          onlyPagination,
          setAction
        ),
      })
      .pipe(
        map((res) => {
          this.logger.debug(res);
          return <K>res;
        }),
        catchError((err) => {
          this.logger.error(err);
          throw err;
        })
      );
  }

  /**
   * Put dati. Usare il Generic "T" per definire il tipo di dati in ingresso ed il Generic "K" per il tipo di dati in uscita
   * @param endpoint
   * @param data
   * @protected
   */
  protected put<T, K>(
    endpoint: string,
    data?: T,
    includePagination: boolean = false,
    includeMeta: boolean = false,
    includeCount: boolean = false,
    onlyPagination: boolean = false,
    setAction: string = ''
  ) {
    return this.http
      .put(this.getUrl(endpoint), data, {
        headers: this.getHeaders(
          includePagination,
          includeMeta,
          includeCount,
          onlyPagination,
          setAction
        ),
      })
      .pipe(
        map((res) => {
          this.logger.debug(res);
          return <K>res;
        }),
        catchError((err) => {
          this.logger.error(err);
          throw err;
        })
      );
  }

  /**
   * Elimina risorsa
   * @param endpoint
   * @protected
   */
  protected delete<T>(
    endpoint: string,
    data?: T,
    includePagination: boolean = false,
    includeMeta: boolean = false,
    includeCount: boolean = false,
    onlyPagination: boolean = false,
    setAction: string = ''
  ) {
    return this.http
      .delete(this.getUrl(endpoint), {
        headers: this.getHeaders(
          includePagination,
          includeMeta,
          includeCount,
          onlyPagination,
          setAction
        ),
        body: data,
      })
      .pipe(
        map((res) => {
          this.logger.debug(res);
          return <T>res;
        }),
        catchError((err) => {
          this.logger.error(err);
          throw err;
        })
      );
  }

  protected get dateFormat() {
    return this._dateFormat;
  }

  /**
   * @description Ritorna l'url completo della chiamata
   * @param endpoint
   * @private
   */
  private getUrl(endpoint: string) {
    return this._host + endpoint;
  }

  /**
   * @description Header delle chiamate
   * @param includePagination
   * @param includeMeta
   * @param includeCount
   * @param onlyPagination
   * @param setAction
   * @private
   */
  private getHeaders(
    includePagination: boolean = false,
    includeMeta: boolean = false,
    includeCount: boolean = false,
    onlyPagination: boolean = false,
    setAction: string = ''
  ) {
    const headers = { ...this._defaultHeaders };

    if (includePagination) {
      headers['X-Include-Pagination'] = 1;
    }

    if (includeMeta) {
      headers['X-Include-Meta'] = 1;
    }

    if (includeCount) {
      headers['X-Include-Count'] = 1;
    }

    if (onlyPagination) {
      headers['X-Pagination'] = 1;
    }

    if (setAction) {
      headers['X-Action'] = setAction;
    }

    return new HttpHeaders(headers);
  }
}
